import React, { useState } from "react";
import {
  Box,
  Typography,
  Popover,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useAuth from "../../../../../hooks/useAuth";

const ROLE_LABELS = {
  PMO:        "PMO",
  SCG:        "SCG",
  MACleaders: "MAC Leader",
  PSIadmin:   "Admin",
};

const ROLE_COLOR_KEY = {
  PMO:        "accent",
  SCG:        "accent",
  MACleaders: "gold",
  PSIadmin:   "gold",
};

const ChevronIcon = ({ open }) => (
  <svg
    width="9" height="9" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.18s ease" }}
  >
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const LogOutIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/>
    <line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getFirstName = (fullName) => {
  if (!fullName) return "User";
  return fullName.trim().split(" ")[0];
};

const UserMenu = ({ onBar }) => {
  const theme  = useTheme();
  const t      = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const { user, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen  = (e) => setAnchorEl(e.currentTarget);
  const handleClose = ()  => setAnchorEl(null);

  const roleLabel    = ROLE_LABELS[user?.role]    || user?.role || "—";
  const roleColorKey = ROLE_COLOR_KEY[user?.role] || "accent";
  const roleColor    = t[roleColorKey];
  const initials     = getInitials(user?.fullName);
  const firstName    = getFirstName(user?.fullName);

  // Colors for trigger on teal AppBar vs normal surface
  const nameColor    = onBar ? "rgba(255,255,255,0.80)" : t.text2;
  const chevronColor = onBar ? "rgba(255,255,255,0.40)" : t.text3;
  const hoverBg      = onBar ? "rgba(255,255,255,0.08)" : t.surface2;
  const activeBg     = onBar ? "rgba(255,255,255,0.12)" : t.surface2;
  const borderLeft   = onBar ? "1px solid rgba(255,255,255,0.10)" : `1px solid ${t.border}`;

  return (
    <>
      {/* ── Trigger: [PMO]  Sindhu  ▾  (flat, no avatar in trigger) ── */}
      <Box
        onClick={handleOpen}
        sx={{
          display:    "flex",
          alignItems: "center",
          gap:        "8px",
          height:     "100%",
          padding:    "0 14px",
          cursor:     "pointer",
          borderLeft: borderLeft,
          background: open ? activeBg : "transparent",
          transition: "background 0.15s ease",
          "&:hover":  { background: hoverBg },
        }}
      >
        {/* Role badge */}
        <Box
          sx={{
            fontFamily:    "'DM Mono', monospace",
            fontSize:      "9.5px",
            fontWeight:    600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color:         roleColor,
            background:    `${roleColor}25`,
            border:        `1px solid ${roleColor}40`,
            borderRadius:  "3px",
            padding:       "2px 7px",
            userSelect:    "none",
            whiteSpace:    "nowrap",
          }}
        >
          {roleLabel}
        </Box>

        {/* First name */}
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize:   "13px",
            fontWeight: 400,
            color:      nameColor,
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          {firstName}
        </Typography>

        {/* Chevron */}
        <Box sx={{ color: chevronColor }}>
          <ChevronIcon open={open} />
        </Box>
      </Box>

      {/* ── Dropdown popover (keeps full avatar + identity + links) ── */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top",    horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt:           "0px",
              width:        "220px",
              background:   t.surface,
              border:       `1px solid ${t.border2}`,
              borderRadius: "0 0 8px 8px",
              boxShadow:    isDark
                ? "0 16px 48px rgba(0,0,0,0.6)"
                : "0 8px 32px rgba(3,81,77,0.12)",
              overflow:     "hidden",
            },
          },
        }}
      >
        {/* Identity block with avatar */}
        <Box
          sx={{
            padding:      "14px 16px 12px",
            borderBottom: `1px solid ${t.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Avatar
              src={user?.photoUrl || undefined}
              sx={{
                width:      "36px",
                height:     "36px",
                fontSize:   "12px",
                fontFamily: "'DM Mono', monospace",
                fontWeight: 600,
                background: `${roleColor}22`,
                color:      roleColor,
                border:     `1px solid ${roleColor}44`,
              }}
            >
              {!user?.photoUrl && initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily:    "'Inter', sans-serif",
                  fontSize:      "13px",
                  fontWeight:    600,
                  color:         t.text,
                  letterSpacing: "-0.01em",
                  overflow:      "hidden",
                  textOverflow:  "ellipsis",
                  whiteSpace:    "nowrap",
                }}
              >
                {user?.fullName || "User"}
              </Typography>
              <Typography
                sx={{
                  fontSize:     "10.5px",
                  color:        t.text3,
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace:   "nowrap",
                }}
              >
                {user?.email || ""}
              </Typography>
            </Box>
          </Box>

          {/* Role badge in popover */}
          <Box
            sx={{
              mt:            "10px",
              display:       "inline-flex",
              alignItems:    "center",
              gap:           "5px",
              background:    `${roleColor}18`,
              border:        `1px solid ${roleColor}33`,
              borderRadius:  "3px",
              padding:       "3px 8px",
              fontFamily:    "'DM Mono', monospace",
              fontSize:      "9px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color:         roleColor,
            }}
          >
            <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", background: roleColor }} />
            {roleLabel}
          </Box>
        </Box>

        {/* Menu items */}
        <Box sx={{ padding: "6px" }}>
          

          

          <Box
            onClick={() => { handleClose(); logout(); }}
            sx={{
              display:      "flex",
              alignItems:   "center",
              gap:          "10px",
              padding:      "8px 10px",
              borderRadius: "4px",
              cursor:       "pointer",
              color:        t.text2,
              fontSize:     "12px",
              transition:   "all 0.15s",
              "&:hover":    { background: "rgba(200,64,64,0.08)", color: "#c84040" },
            }}
          >
            <Box sx={{ color: "inherit" }}><LogOutIcon /></Box>
            <span>Sign out</span>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default UserMenu;